import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Lock, Trash2, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/apiErr";
import { PageHeader } from "../../components/PageHeader";

const EMPTY = { name: "", description: "", prompt: "", enabled: true, sort: 100 };
const IN = "w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand disabled:bg-slate-50 disabled:text-slate-500";
const toForm = (c) => ({ name: c.name || "", description: c.description || "", prompt: c.prompt || "", enabled: c.enabled !== false, sort: c.sort ?? 100 });

function validate(f) {
  if (!f.name.trim() || f.name.length > 80) return "Nama wajib 1–80 karakter";
  if (f.description.length > 600) return "Deskripsi maksimal 600 karakter";
  if (f.prompt.length < 20 || f.prompt.length > 20000) return "Prompt wajib 20–20.000 karakter";
  if (!Number.isInteger(Number(f.sort)) || f.sort < 0 || f.sort > 10000) return "Urutan 0–10.000";
  return null;
}

function CharList({ items, selected, onSelect, onNew, readOnly }) {
  return (
    <div className="aivora-card overflow-hidden" data-testid="char-list">
      <div className="flex items-center justify-between border-b border-line p-3">
        <p className="text-sm font-bold text-slate-900">{items.length} karakter</p>
        {!readOnly && <button onClick={onNew} className="btn-grad flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs font-bold" data-testid="char-new"><Plus size={13} /> Baru</button>}
      </div>
      <ul className="max-h-[40vh] divide-y divide-slate-100 overflow-y-auto lg:max-h-none">
        {items.map((c) => (
          <li key={c.id}>
            <button onClick={() => onSelect(c)} data-testid={`char-item-${c.id}`} className={`w-full px-4 py-3 text-left transition-colors ${selected === c.id ? "bg-brand/5" : "hover:bg-slate-50"}`}>
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">{c.builtin && <Lock size={12} className="text-slate-400" />}{c.name}
                {c.builtin ? <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600">Bawaan</span> : !c.enabled && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">Nonaktif</span>}</p>
              <p className="mt-0.5 line-clamp-2 text-[11px] text-slate-400">{c.description || "—"}</p>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Field({ label, hint, children }) {
  return <label className="block"><span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500"><span>{label}</span>{hint && <span className="normal-case tracking-normal text-slate-400">{hint}</span>}</span><div className="mt-1">{children}</div></label>;
}

function CharForm({ char, onSaved, onDeleted, readOnly }) {
  const isNew = !char.id;
  const locked = readOnly || char.builtin;
  const [f, setF] = useState(toForm(char));
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(toForm(char)), [char]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const err = validate(f);
    if (err) return toast.error(err);
    setBusy(true);
    const body = { ...f, name: f.name.trim(), sort: Number(f.sort) };
    try {
      const r = isNew ? await api.post("/platform/persona-characters", body) : await api.put(`/platform/persona-characters/${char.id}`, body);
      toast.success(isNew ? "Karakter dibuat" : "Karakter disimpan"); onSaved(r.data);
    } catch (e) { toast.error(apiErr(e, "Gagal menyimpan karakter")); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Hapus karakter "${char.name}"? Persona pengguna yang memakainya akan kembali ke karakter bawaan.`)) return;
    setBusy(true);
    try { const r = await api.delete(`/platform/persona-characters/${char.id}`); toast.success(`Karakter dihapus · ${r.data.personas_reset ?? 0} persona dikembalikan ke bawaan`); onDeleted(); }
    catch (e) { toast.error(apiErr(e, "Gagal menghapus")); } finally { setBusy(false); }
  };
  return (
    <div className="aivora-card p-5" data-testid="char-form">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-base font-black text-slate-900">{isNew ? "Karakter baru" : char.name}</p>
        {!locked && (
          <div className="flex gap-2">
            {!isNew && <button onClick={del} disabled={busy} className="flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600" data-testid="char-delete"><Trash2 size={13} /> Hapus</button>}
            <button onClick={save} disabled={busy} className="btn-grad flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold disabled:opacity-50" data-testid="char-save">{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Simpan</button>
          </div>
        )}
      </div>
      {char.builtin && <p className="mt-3 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="char-builtin-note"><Lock size={13} /> Karakter bawaan = prompt inti sistem, hanya bisa dibaca.</p>}
      {readOnly && !char.builtin && <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">Mode baca — hanya Super Admin yang dapat mengubah.</p>}
      <div className="mt-4 grid gap-4 md:grid-cols-[1fr_140px_140px]">
        <Field label="Nama" hint={`${f.name.length}/80`}><input className={IN} maxLength={80} disabled={locked} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="char-name" /></Field>
        <Field label="Urutan"><input type="number" min={0} max={10000} className={IN} disabled={locked} value={f.sort} onChange={(e) => set("sort", e.target.value === "" ? "" : Number(e.target.value))} data-testid="char-sort" /></Field>
        <Field label="Status"><button type="button" disabled={locked} onClick={() => set("enabled", !f.enabled)} data-testid="char-enabled" className={`w-full rounded-xl px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60 ${f.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{f.enabled ? "Aktif" : "Nonaktif"}</button></Field>
      </div>
      <div className="mt-4"><Field label="Deskripsi Karakter" hint={`${f.description.length}/600 · tampil di bawah combo box & dipakai AI menyusun profil`}><textarea rows={3} maxLength={600} className={IN} disabled={locked} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="char-description" /></Field></div>
      <div className="mt-4"><Field label="Prompt Karakter" hint={`${f.prompt.length.toLocaleString("id-ID")}/20.000 · {{language}} = placeholder bahasa`}><textarea rows={18} maxLength={20000} className={`${IN} font-mono text-xs leading-relaxed`} disabled={locked} value={f.prompt} onChange={(e) => set("prompt", e.target.value)} data-testid="char-prompt" /></Field></div>
      {!f.enabled && !locked && <p className="mt-3 flex items-center gap-2 text-xs text-amber-700"><AlertTriangle size={13} /> Nonaktif: tidak muncul untuk pengguna; persona yang memakainya jatuh ke bawaan.</p>}
    </div>
  );
}

export default function PlatformCharacters({ readOnly }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [sel, setSel] = useState(null);
  const load = useCallback((pickId) => api.get("/platform/persona-characters").then((r) => {
    setItems(r.data.items); setError(null);
    setSel((s) => r.data.items.find((c) => c.id === (pickId ?? s?.id)) || r.data.items[0] || null);
  }).catch((e) => { setItems([]); setError(apiErr(e, "Gagal memuat karakter persona")); }), []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Memuat…</p>;
  return (
    <div data-testid="platform-characters">
      <PageHeader title="Karakter Persona" description="Preset yang dipilih pengguna saat membuat persona. Prompt karakter menimpa prompt inti bawaan (chat teks & suara Realtime); instruksi bahasa, memori, tools & profil tetap berlaku." />
      {error && <p className="mt-4 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="char-error">{error}</p>}
      <div className="mt-6 grid items-start gap-5 lg:grid-cols-[320px_1fr]">
        <CharList items={items} selected={sel?.id ?? "__new"} onSelect={setSel} onNew={() => setSel({ ...EMPTY })} readOnly={readOnly} />
        {sel && <CharForm char={sel} readOnly={readOnly} onSaved={(c) => load(c.id)} onDeleted={() => load("default")} />}
      </div>
    </div>
  );
}
